export interface UrbanAgentStartRequest {
  prompt: string;
  style?: string;
  era?: string;
  region?: string;
  maxResponseTokens?: number;
  temperature?: number;
}

export interface UrbanAgentSession {
  sessionId: string;
  agentType: UrbanAgentType;
  currentPrompt: string;
  iterations: UrbanAgentIteration[];
  metadata?: Record<string, any>;
}

export interface UrbanAgentIteration {
  iterationNumber: number;
  refinedPrompt: string;
  previousPrompt: string;
  improvementExplanation?: string;
  timestamp: string;
  subAgentType?: string;
}

export interface UrbanAgentIterateRequest {
  feedback?: string;
  style?: string;
  era?: string;
  region?: string;
  maxResponseTokens?: number;
  temperature?: number;
}

export interface UrbanAgentIterateResponse {
  sessionId: string;
  iterationNumber: number;
  refinedPrompt: string;
  previousPrompt?: string;
  improvementExplanation?: string;
  success?: boolean;
  errors?: string | null;
}

export enum UrbanAgentType {
  Animation = 'animation',
  Documentary = 'documentary',
  Music = 'music',
  StreetArt = 'streetart',
  General = 'general'
}

export interface UrbanAgentConfig {
  id: UrbanAgentType;
  name: string;
  description: string;
  endpoint: string;
  icon: string;
  color: string;
  features: string[];
  specialEndpoints?: Record<string, string>;
}

export const URBAN_AGENTS: UrbanAgentConfig[] = [
  {
    id: UrbanAgentType.Animation,
    name: 'Urban Animation Studio',
    description: 'Create animated urban culture scenes with diverse styles and eras',
    endpoint: '/api/imageprompt/urban/animation/session/start',
    icon: '🎬',
    color: '#FF6B6B',
    features: [
      'Animation style glossary',
      'Audience adaptation',
      'Style comparison & fusion',
      'Era-specific animations'
    ],
    specialEndpoints: {
      glossary: '/api/imageprompt/urban/animation/animation-glossary',
      audienceAdaptation: '/api/imageprompt/urban/animation/audience-adaptation',
      styleComparison: '/api/imageprompt/urban/animation/style-comparison',
      styleFusion: '/api/imageprompt/urban/animation/style-fusion'
    }
  },
  {
    id: UrbanAgentType.Documentary,
    name: 'Urban Documentary',
    description: 'Generate documentary-style urban photography prompts',
    endpoint: '/api/imageprompt/urban/documentary/session/start',
    icon: '📸',
    color: '#4ECDC4',
    features: [
      'Community portraits',
      'Crisis documentation',
      'Moment generation',
      'Social narratives'
    ],
    specialEndpoints: {
      communityPortrait: '/api/imageprompt/urban/documentary/community/portrait',
      crisisDocument: '/api/imageprompt/urban/documentary/crisis/document',
      momentGenerate: '/api/imageprompt/urban/documentary/moment/generate'
    }
  },
  {
    id: UrbanAgentType.Music,
    name: 'Urban Music Visualizer',
    description: 'Visualize urban music scenes, concerts, and studio environments',
    endpoint: '/api/imageprompt/urban/music/session/start',
    icon: '🎵',
    color: '#9B59B6',
    features: [
      'Concert scene generation',
      'Studio scene creation',
      'Track visualization',
      'Music culture representation'
    ],
    specialEndpoints: {
      concertScene: '/api/imageprompt/urban/music/concert-scene',
      studioScene: '/api/imageprompt/urban/music/studio-scene',
      visualizeTrack: '/api/imageprompt/urban/music/visualize-track'
    }
  },
  {
    id: UrbanAgentType.StreetArt,
    name: 'Urban Street Art Creator',
    description: 'Design street art, graffiti, and urban art installations',
    endpoint: '/api/imageprompt/urban/streetart/session/start',
    icon: '🎨',
    color: '#F39C12',
    features: [
      'Crew battle scenes',
      'Street art generation',
      'Graffiti styles',
      'Urban art movements'
    ],
    specialEndpoints: {
      crewBattle: '/api/imageprompt/urban/streetart/crew-battle',
      generatePiece: '/api/imageprompt/urban/streetart/generate-piece'
    }
  },
  {
    id: UrbanAgentType.General,
    name: 'Urban Culture Explorer',
    description: 'General urban culture visualization with regional and era variations',
    endpoint: '/api/imageprompt/urban/session/start',
    icon: '🏙️',
    color: '#3498DB',
    features: [
      'Cultural glossary',
      'Era evolution',
      'Regional comparison',
      'Scene breakdown'
    ],
    specialEndpoints: {
      culturalGlossary: '/api/imageprompt/urban/cultural-glossary',
      eraEvolution: '/api/imageprompt/urban/era-evolution',
      regionalComparison: '/api/imageprompt/urban/regional-comparison',
      sceneBreakdown: '/api/imageprompt/urban/scene-breakdown'
    }
  }
];